import { useState, useEffect } from 'react'
import {UserPlus, Check, AlertCircle} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useNavigate } from 'react-router-dom'

export default function AdminSetup() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'form' | 'otp' | 'success'>('form')
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    userName: 'Administrador'
  })
  const [otp, setOtp] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleSendOtp = async () => {
    if (!formData.email) {
      setError('Por favor, insira seu e-mail')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem')
      return
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres')
      return
    }

    setLoading(true)
    setError('')

    try {
      await lumi.auth.sendOtp({ 
        email: formData.email, 
        type: 'register' 
      })
      setStep('otp')
      setCountdown(60)
    } catch (err: any) {
      setError(err?.message || 'Erro ao enviar código de verificação')
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!otp || otp.length < 4) {
      setError('Por favor, insira o código de verificação')
      return
    }

    setLoading(true)
    setError('')

    try {
      await lumi.auth.signUpWithPassword({
        email: formData.email,
        password: formData.password,
        otp: otp,
        userName: formData.userName
      })

      setStep('success')
      
      // Redirecionar para o painel admin após 2 segundos
      setTimeout(() => {
        navigate('/admin')
      }, 2000)
    } catch (err: any) {
      setError(err?.message || 'Erro ao criar conta de administrador')
    } finally {
      setLoading(false)
    }
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-deep via-violet-medium to-rose-deep/40 flex items-center justify-center px-6">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12 w-full max-w-md border border-violet-dust/30 text-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          
          <h1 className="font-serif text-3xl font-bold text-violet-deep mb-4">
            Conta Criada com Sucesso!
          </h1>
          <p className="font-sans text-gray-600 mb-6">
            Sua conta de administrador foi configurada.
            Redirecionando para o painel...
          </p>
          
          <div className="animate-spin w-8 h-8 border-4 border-violet-deep border-t-transparent rounded-full mx-auto"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-deep via-violet-medium to-rose-deep/40 flex items-center justify-center px-6 py-12">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12 w-full max-w-md border border-violet-dust/30">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-violet-100 mx-auto mb-6">
          <UserPlus className="w-8 h-8 text-violet-deep" />
        </div>
        
        <h1 className="font-serif text-3xl font-bold text-violet-deep text-center mb-2">
          Configuração Inicial
        </h1>
        <p className="font-sans text-gray-600 text-center mb-8">
          {step === 'form' 
            ? 'Crie sua conta de administrador'
            : 'Verifique seu e-mail e insira o código'
          }
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="font-sans text-sm">{error}</p>
          </div>
        )}

        {step === 'form' ? (
          <div className="space-y-5">
            <div>
              <label className="block font-sans text-gray-700 font-medium mb-2">
                Nome do Administrador
              </label>
              <input
                type="text"
                value={formData.userName}
                onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                placeholder="Mônica Maria"
              />
            </div>

            <div>
              <label className="block font-sans text-gray-700 font-medium mb-2">
                E-mail
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                placeholder="monica@reacendaluz.com.br"
              />
            </div>

            <div>
              <label className="block font-sans text-gray-700 font-medium mb-2">
                Senha
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                placeholder="••••••••"
                minLength={6}
              />
              <p className="font-sans text-xs text-gray-500 mt-1">Mínimo de 6 caracteres</p>
            </div>

            <div>
              <label className="block font-sans text-gray-700 font-medium mb-2">
                Confirmar Senha
              </label>
              <input
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans"
                placeholder="••••••••"
                minLength={6}
              />
            </div>

            <button
              type="button"
              onClick={handleSendOtp}
              disabled={loading}
              className="group relative w-full bg-gradient-to-r from-violet-deep via-violet-medium to-rose-deep hover:from-rose-deep hover:via-violet-medium hover:to-violet-deep text-white font-sans font-bold text-lg py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(139,92,246,0.6)] hover:shadow-[0_0_50px_rgba(139,92,246,1)] transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="relative z-10">
                {loading ? 'Enviando...' : 'Enviar Código de Verificação'}
              </span>
              <div className="absolute inset-0 bg-white/20 rounded-full blur-lg group-hover:blur-xl transition-all"></div>
            </button>
          </div>
        ) : (
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4">
              <p className="font-sans text-sm text-violet-900 text-center">
                Um código foi enviado para:<br />
                <strong>{formData.email}</strong>
              </p>
            </div>

            <div>
              <label className="block font-sans text-gray-700 font-medium mb-2">
                Código de Verificação
              </label>
              <input
                type="text"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-violet-medium focus:outline-none transition-colors font-sans text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
              />
            </div>

            <button
              type="button"
              onClick={handleSendOtp}
              disabled={countdown > 0 || loading}
              className="w-full font-sans text-violet-deep hover:text-violet-medium transition-colors text-sm underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {countdown > 0 ? `Reenviar em ${countdown}s` : 'Reenviar Código'}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="group relative w-full bg-gradient-to-r from-violet-deep via-violet-medium to-rose-deep hover:from-rose-deep hover:via-violet-medium hover:to-violet-deep text-white font-sans font-bold text-lg py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(139,92,246,0.6)] hover:shadow-[0_0_50px_rgba(139,92,246,1)] transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="relative z-10">
                {loading ? 'Criando Conta...' : 'Criar Conta de Administrador'}
              </span>
              <div className="absolute inset-0 bg-white/20 rounded-full blur-lg group-hover:blur-xl transition-all"></div>
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="font-sans text-xs text-gray-500 text-center">
            Esta é uma configuração única para criar o acesso administrativo.
          </p>
        </div>
      </div>
    </div>
  )
}
